import { ChevronRight, MessageSquare, Users } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDdsMetrics } from '@/api/queries/kpi-metrics-queries'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type {
	KpiDDSBranchCrEntry,
	KpiDDSBranchEntry,
	KpiDDSCoverageBranchEntry,
	KpiMetricsParams,
} from '@/types'
import { DdsAttendanceDialog } from './dds-attendance-dialog'
import { AccordionBarChart } from './shared/accordion-bar-chart'
import { KpiMetricsDashboardLoading } from './shared/kpi-metrics-dashboard-loading'
import { KpiStatCard } from './shared/kpi-stat-card'

const TYPE_COLORS: Record<string, string> = {
	DDS: 'bg-kaefer-sapphire-blue',
	PIT_STOP: 'bg-kaefer-violet',
	DMI: 'bg-kaefer-honey-yellow',
	DIG: 'bg-kaefer-forest-green',
	DDS_GERAL: 'bg-kaefer-medium-grey',
}

const TYPE_ICON_COLORS: Record<string, string> = {
	DDS: 'bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue',
	PIT_STOP: 'bg-kaefer-violet/15 text-kaefer-violet',
	DMI: 'bg-kaefer-honey-yellow/20 text-kaefer-dark-grey',
	DIG: 'bg-kaefer-forest-green/15 text-kaefer-forest-green',
	DDS_GERAL: 'bg-kaefer-medium-grey/20 text-kaefer-dark-grey',
}

const TYPE_LABELS: Record<string, string> = {
	DDS: 'DDS',
	PIT_STOP: 'PIT STOP',
	DMI: 'DMI',
	DIG: 'DIG',
	DDS_GERAL: 'DDS GERAL',
}

const FALLBACK_TYPE_COLOR = 'bg-kaefer-medium-grey'

function typeLabel(type: string) {
	return TYPE_LABELS[type] ?? type
}

function typeColor(type: string) {
	return TYPE_COLORS[type] ?? FALLBACK_TYPE_COLOR
}

function countForType(
	entry: KpiDDSBranchEntry | KpiDDSBranchCrEntry,
	type: string,
) {
	const value = entry[type]
	return typeof value === 'number' ? value : 0
}

export function DdsDashboard({ params }: { params?: KpiMetricsParams }) {
	const { t } = useTranslation()
	const { data: dds, isLoading } = useDdsMetrics(params)
	const [selectedCr, setSelectedCr] = useState<{
		id: string
		name: string
	} | null>(null)

	if (isLoading || !dds) return <KpiMetricsDashboardLoading />

	const byType = Object.entries(dds.byType ?? {})
		.map(([type, count]) => ({ type, count }))
		.sort((a, b) => b.count - a.count)
	const types = byType.map((i) => i.type)

	const byBranch = [...(dds.byBranch ?? [])]
		.sort((a, b) => b.count - a.count)
		.map((b) => ({
			...b,
			crList: [...(b.crList ?? [])].sort((x, y) => y.count - x.count),
		}))

	const coverageByBranch = dds.coverage?.byBranch ?? []

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
				<KpiStatCard
					icon={<MessageSquare className="size-4" />}
					iconClass="bg-kaefer-dark-grey/10 text-kaefer-dark-grey"
					label={t('kpiMetrics.dds.total')}
					value={String(dds.total)}
				/>
				{byType.map((item) => (
					<KpiStatCard
						key={item.type}
						icon={<MessageSquare className="size-4" />}
						iconClass={
							TYPE_ICON_COLORS[item.type] ??
							'bg-kaefer-medium-grey/20 text-kaefer-dark-grey'
						}
						label={typeLabel(item.type)}
						value={String(item.count)}
					/>
				))}
			</div>

			<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{t('kpiMetrics.dds.byBranch')}
				</span>
				<TypeLegend types={types} />
				<div className="mt-1 max-h-96 overflow-y-auto pr-1">
					<AccordionBarChart
						items={byBranch.map((b) => ({
							id: b.branchId,
							label: b.branchName,
							value: b.count,
							children: b.crList,
						}))}
						getChildId={(cr) => cr.crId}
						getChildLabel={(cr) => cr.crName}
						getChildValue={(cr) => cr.count}
						emptyState={<EmptyState t={t} />}
						renderRow={(item, maxParent) => {
							const branch = byBranch.find((b) => b.branchId === item.id)
							return (
								<TypeStackedBarRow
									label={item.label}
									total={item.value}
									max={maxParent}
									segments={types.map((type) => ({
										type,
										count: branch ? countForType(branch, type) : 0,
									}))}
								/>
							)
						}}
						renderChildren={(item) => {
							const children = item.children ?? []
							const maxChild = Math.max(1, ...children.map((cr) => cr.count))
							return (
								<div className="flex flex-col gap-2">
									{children.map((cr) => (
										<TypeStackedBarRow
											key={cr.crId}
											label={cr.crName}
											total={cr.count}
											max={maxChild}
											segments={types.map((type) => ({
												type,
												count: countForType(cr, type),
											}))}
											muted
										/>
									))}
								</div>
							)
						}}
					/>
				</div>
			</div>

			<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{t('kpiMetrics.dds.coverageByBranch')}
				</span>
				{coverageByBranch.length === 0 ? (
					<EmptyState t={t} />
				) : (
					<div className="mt-3">
						<CoverageTable
							branches={coverageByBranch}
							t={t}
							onSelectCr={setSelectedCr}
						/>
					</div>
				)}
			</div>

			<DdsAttendanceDialog
				cr={selectedCr}
				params={params}
				onOpenChange={(open) => !open && setSelectedCr(null)}
			/>
		</div>
	)
}

function TypeStackedBarRow({
	label,
	total,
	max,
	segments,
	muted,
}: {
	label: string
	total: number
	max: number
	segments: { type: string; count: number }[]
	muted?: boolean
}) {
	const widthPct = Math.max((total / max) * 100, total > 0 ? 2 : 0)
	const visible = segments.filter((s) => s.count > 0)

	return (
		<div className="grid grid-cols-[1fr_2fr_auto] items-center gap-2.5">
			<span
				className={cn(
					'truncate text-left text-xs',
					muted ? 'text-muted-foreground' : 'text-kaefer-dark-grey',
				)}
				title={label}
			>
				{label}
			</span>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="h-3.5 overflow-hidden bg-kaefer-light-silver/60">
						<div className="flex h-full" style={{ width: `${widthPct}%` }}>
							{visible.map((s) => (
								<div
									key={s.type}
									className={cn('h-full', typeColor(s.type))}
									style={{ width: `${(s.count / total) * 100}%` }}
								/>
							))}
						</div>
					</div>
				</TooltipTrigger>
				<TooltipContent side="top" align="center">
					<div className="flex flex-col gap-1">
						{visible.map((s) => (
							<div key={s.type} className="flex items-center gap-4 text-xs">
								<span className="flex items-center gap-1.5">
									<span
										className={cn(
											'size-2 shrink-0 rounded-full',
											typeColor(s.type),
										)}
									/>
									{typeLabel(s.type)}
								</span>
								<span className="ml-auto tabular-nums text-muted-foreground">
									{s.count} ({Math.round((s.count / total) * 100)}%)
								</span>
							</div>
						))}
					</div>
				</TooltipContent>
			</Tooltip>
			<span className="text-right text-xs font-semibold text-muted-foreground tabular-nums">
				{total}
			</span>
		</div>
	)
}

function TypeLegend({ types }: { types: string[] }) {
	if (types.length === 0) return null
	return (
		<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
			{types.map((type) => (
				<span key={type} className="flex items-center gap-1.5">
					<span className={cn('size-2.5 rounded-[2px]', typeColor(type))} />
					{typeLabel(type)}
				</span>
			))}
		</div>
	)
}

function CoverageBar({ pct }: { pct: number }) {
	return (
		<div className="flex items-center justify-end gap-2">
			<div className="h-2 w-16 overflow-hidden rounded-full bg-kaefer-light-silver/60">
				<div
					className="h-full rounded-full bg-kaefer-sapphire-blue"
					style={{ width: `${Math.min(pct, 100)}%` }}
				/>
			</div>
			<span className="w-12 text-right tabular-nums text-xs text-kaefer-dark-grey">
				{pct.toFixed(1)}%
			</span>
		</div>
	)
}

function CoverageTable({
	branches,
	t,
	onSelectCr,
}: {
	branches: KpiDDSCoverageBranchEntry[]
	t: (key: string) => string
	onSelectCr: (cr: { id: string; name: string }) => void
}) {
	const [expanded, setExpanded] = useState<Set<string>>(new Set())

	const toggle = (branchId: string) =>
		setExpanded((current) => {
			const next = new Set(current)
			if (next.has(branchId)) next.delete(branchId)
			else next.add(branchId)
			return next
		})

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>{t('kpiMetrics.dds.tableBranchCr')}</TableHead>
					<TableHead className="text-right">
						{t('kpiMetrics.dds.tableAttended')}
					</TableHead>
					<TableHead className="text-right">
						{t('kpiMetrics.dds.tableHeadcount')}
					</TableHead>
					<TableHead className="text-right">
						{t('kpiMetrics.dds.tableDdsDays')}
					</TableHead>
					<TableHead className="text-right">
						{t('kpiMetrics.dds.tableExpected')}
					</TableHead>
					<TableHead className="text-right">
						{t('kpiMetrics.dds.tableCoverage')}
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{branches.map((branch) => {
					const crList = branch.crList ?? []
					const isOpen = expanded.has(branch.branchId)
					const hasCrs = crList.length > 0
					return (
						<Fragment key={branch.branchId}>
							<TableRow
								className={cn(hasCrs && 'cursor-pointer')}
								onClick={() => hasCrs && toggle(branch.branchId)}
							>
								<TableCell className="font-semibold text-kaefer-dark-grey">
									<span className="flex items-center gap-1.5">
										<ChevronRight
											className={cn(
												'size-4 shrink-0 text-muted-foreground transition-transform',
												isOpen && 'rotate-90',
												!hasCrs && 'invisible',
											)}
										/>
										{branch.branchName}
									</span>
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{branch.attended}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{branch.headcount}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{branch.ddsTotal} / {branch.ddsExpected}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{branch.expected}
								</TableCell>
								<TableCell>
									<CoverageBar pct={branch.coveragePct} />
								</TableCell>
							</TableRow>
							{isOpen &&
								crList.map((cr) => (
									<TableRow
										key={cr.crId}
										className="group cursor-pointer bg-muted/30 text-kaefer-dark-grey"
										title={t('kpiMetrics.dds.attendance.open')}
										onClick={() => onSelectCr({ id: cr.crId, name: cr.crName })}
									>
										<TableCell className="pl-10 font-medium">
											<span className="flex items-center gap-1.5">
												{cr.crName}
												<Users className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
											</span>
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{cr.attended}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{cr.headcount}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{cr.ddsTotal} / {cr.ddsExpected}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{cr.expected}
										</TableCell>
										<TableCell>
											<CoverageBar pct={cr.coveragePct} />
										</TableCell>
									</TableRow>
								))}
						</Fragment>
					)
				})}
			</TableBody>
		</Table>
	)
}

function EmptyState({ t }: { t: (key: string) => string }) {
	return (
		<p className="py-10 text-center text-sm text-muted-foreground">
			{t('kpiMetrics.charts.noData')}
		</p>
	)
}
